import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PictureInPicture,
  List as ListIcon,
  ListAlt,
  Task,
  Schedule,
  WebAsset,
  People,
  CrisisAlert as PendingIcon,
  AutoAwesomeMosaic as MachinModelIcon,
  Assignment as AllServiceTickets,
} from "@mui/icons-material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import GroupIcon from "@mui/icons-material/Group";
import StoreIcon from "@mui/icons-material/Store";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import DevicesIcon from "@mui/icons-material/Devices";
import BuildIcon from "@mui/icons-material/Build";

import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { Links } from "utils/pageLinkNames";
import config from "app/config";

const navItems = [
  { text: "Dashboards" },
  { text: "Plant Overview", navigateTo: Links.PLANT_OVERVIEW, icon: <AssessmentIcon /> },
  { text: "Breakdown Hours", navigateTo: Links.BREAKDOWN, icon: <EventNoteIcon /> },
  { text: "Tickets Overview", navigateTo: Links.SERVICE_DASHBOARD, icon: <AssignmentTurnedInIcon /> },
  { text: "MTBF & MTTR", navigateTo: Links.MTBF_VS_MTTR, icon: <BuildIcon /> },
  { text: "Service Tickets" },
  { text: "Active", navigateTo: Links.ASSIGN_TECHNICIAN_SERVICE_TICKETS, icon: <BuildCircleIcon /> },
  { text: "Action Required", navigateTo: Links.ACTION_REQUIRED_SERVICE_TICKETS, icon: <Task /> },
  { text: "Pending", navigateTo: Links.PENDING_SERVICE_TICKETS, icon: <PendingIcon /> },
  { text: "All Service Tickets", navigateTo: Links.SERVICE_TICKETS_TAB, icon: <AllServiceTickets /> },
  { text: "Data" },
  { text: "Reports", navigateTo: Links.REPORTS, icon: <DescriptionIcon /> },
  { text: "Scheduling", navigateTo: Links.SCHEDULING, icon: <Schedule /> },
  { text: "Assets", navigateTo: Links.ASSETS, icon: <DevicesIcon /> },
  { text: "Users - Departments", navigateTo: Links.USER, icon: <GroupIcon /> },
  { text: "Vendors - Suppliers", navigateTo: Links.VENDORS, icon: <StoreIcon /> },
  { text: "Spare Parts", navigateTo: Links.INVENTORY, icon: <Inventory2Icon /> },
  { text: "Locations", navigateTo: Links.LOCATIONS, icon: <LocationOnIcon /> },
  { text: "Templates", navigateTo: Links.TEMPLATES, icon: <PictureInPicture /> },
  { text: "Machine Models", navigateTo: Links.MACHINE_MODELS, icon: <MachinModelIcon /> },
];

const navItemsFinance = [
  // Finance role navigation items...
  {
    text: "Data",
  },
  {
    text: "Assets",
    navigateTo: Links.ASSETS,
    icon: <WebAsset />,
  },
  {
    text: "Service Tickets",
  },
  {
    text: "Service Tickets",
    navigateTo: Links.ALL_SERVICE_TICKETS,
    icon: <WebAsset />,
  },
];

const navItemsSvcTech = [
  // Svc Technician role navigation items...
  {
    text: "Service Tickets",
  },
  {
    text: "Service Tickets",
    navigateTo: Links.ALL_SERVICE_TICKETS,
    icon: <WebAsset />,
  },
];

// Operators should see exactly what Service Technicians see (same navigation).

const navItemsStoresUser = [
  // Stores user role navigation items...
  {
    text: "Service Tickets",
  },
  {
    text: "Assigned",
    navigateTo: Links.ASSIGNED,
    icon: <ListIcon />,
  },
  {
    text: "Spares Required",
    navigateTo: Links.SPARES_REQUIRED,
    icon: <ListIcon />,
  },
  {
    text: "Spares Recommended",
    navigateTo: Links.SPARES_RECOMMENDED,
    icon: <ListIcon />,
  },
  {
    text: "Data",
  },
  {
    text: "Spare Parts",
    navigateTo: Links.INVENTORY,
    icon: <ListAlt />,
  },
];

const navItemsIt = [
  // IT role navigation items...
  {
    text: "Data",
  },
  {
    text: "Users",
    navigateTo: Links.USER,
    icon: <People />,
  },
  {
    text: "Service Tickets",
  },
  {
    text: "Service Tickets",
    navigateTo: Links.ALL_SERVICE_TICKETS,
    icon: <WebAsset />,
  },
];

const userHasRole = (user, role) => Array.isArray(user.roles) && user.roles.includes(role);

// Helper function to render nav items
const renderNavItems = (navItems, active, onClickItem, theme) =>
  navItems.map(({ text, icon, navigateTo }, index) => {
    // Section heading: no icon, no navigateTo
    if (!icon && !navigateTo) {
      return (
        <ListItem key={text} disablePadding sx={{ mb: 0.25, mt: index === 0 ? 0 : 0.9 }}>
          <ListItemText
            primary={text}
            primaryTypographyProps={{
              variant: "subtitle2",
              style: {
                fontWeight: "bolder",
                letterSpacing: 1,
                fontSize: 14,
                color: theme.palette.secondary[200],
                textTransform: "uppercase",
              },
            }}
            sx={{ pl: 2, mb: 0.1 }}
          />
        </ListItem>
      );
    }
    // Normal clickable nav item
    return (
      <ListItem key={text} disablePadding sx={{ mb: 0.1 }}>
        <ListItemButton
          onClick={() => (navigateTo ? onClickItem(navigateTo) : null)}
          sx={{
            py: 0.42,
            pr: "2rem",
            borderRadius: "12px",

            background: active === navigateTo ? theme.palette.secondary[300] : "rgba(255,255,255,0.02)",
            color: active === navigateTo ? "#fff" : theme.palette.secondary[100],
            boxShadow: active === navigateTo ? "0 4px 20px 0 rgba(0,0,0,0.10)" : "none",
            transition: "all 0.2s",
            alignItems: "center",
            "&:hover": {
              background: theme.palette.secondary[200],
              color: theme.palette.primary.dark,
              boxShadow: "0 2px 8px 0 rgba(0,0,0,0.08)",
            },
          }}
        >
          {icon && (
            <ListItemIcon
              sx={{
                minWidth: 36,
                mr: 1.25,
                color: active === navigateTo ? "#fff" : theme.palette.appColors.sideBarText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
              }}
            >
              {icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={text}
            primaryTypographyProps={{
              variant: "body1",
              style: {
                fontWeight: 500,
                letterSpacing: 0.5,
                fontSize: 16,
              },
            }}
            sx={{
              color: active === navigateTo ? "#fff" : theme.palette.appColors.sideBarText,
              ml: 0.5,
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  });

const Sidebar = (props) => {
  const { pathname } = useLocation();
  const { appConstants, license } = useSelector((state) => state.service);
  const { user } = useSelector((state) => state.auth);

  let navigate = useNavigate();

  const theme = useTheme();
  const [active, setActive] = useState("");

  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);

  const onClickItem = (navigateTo) => {
    navigate(`/${navigateTo}`);
    setActive(navigateTo);
  };

  const isLicenseExpired =
    license?.licenseStatus === appConstants?.allTypes?.LICENSE_STATUS?.EXPIRED || license?.licenseStatus === "Expired";

  const memoizedNavItems = useMemo(() => {
    if (!user || !appConstants?.allTypes?.USER_ROLES) return [];
    if (isLicenseExpired) return [];
    if (userHasRole(user, appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR)) return navItems;
    if (userHasRole(user, appConstants.allTypes.USER_ROLES.ROLE_OPERATOR)) return navItemsSvcTech;
    if (userHasRole(user, appConstants.allTypes.USER_ROLES.ROLE_FINANCE)) return navItemsFinance;
    if (userHasRole(user, appConstants.allTypes.USER_ROLES.ROLE_STORES)) return navItemsStoresUser;
    if (userHasRole(user, appConstants.allTypes.USER_ROLES.ROLE_IT)) return navItemsIt;
    if (userHasRole(user, appConstants.allTypes.USER_ROLES.ROLE_SERVICE_TECHNICIAN)) return navItemsSvcTech;
    return [];
  }, [user, appConstants, isLicenseExpired]);

  return (
    <Box component="nav" sx={{ width: { sm: props.drawerWidth }, flexShrink: { sm: 0 } }} aria-label="mailbox folders">
      <Drawer
        open
        variant="permanent"
        anchor="left"
        transitionDuration={500}
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: props.drawerWidth,
            backgroundColor: theme.palette.appColors.navBarBackground,
            color: "#fff",
            borderTopRightRadius: 15,
            borderBottomRightRadius: 15,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            border: "none",
            padding: "0 0.5rem",
          },
        }}
      >
        <Box width="100%" height="100%" display="flex" flexDirection="column">
          {/* App Name only, no round icon */}
          {/* <Box m="2rem 0 1.5rem 0" display="flex" flexDirection="column" alignItems="center">
            <Typography variant="h2" fontWeight="bold" sx={{ color: "#fff", letterSpacing: 1 }}>
              {appConstants?.appCustomerName || ""}
            </Typography>
          </Box> */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              pt: 3.5,
              pb: 3.5,
              minHeight: "64px",
              height: "64px",
            }}
          >
            <Box
              component="img"
              src={(() => {
                const name = appConstants?.appCustomerName?.toLowerCase();
                return name ? `/logos/${name}.png` : "/logos/mainman.png";
              })()}
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop if default.png is missing
                e.target.src = "/logos/mainman.png";
              }}
              alt="Tenant Logo"
              sx={{
                width: 260,
                objectFit: "contain",
                maxHeight: 140,
              }}
            />
          </Box>

          <List
            sx={{
              flex: 1,
              overflowY: "auto",
              py: 0,
              scrollbarWidth: "thin",
              scrollbarColor: `${theme.palette.secondary[200]} transparent`,
              "&::-webkit-scrollbar": {
                width: "8px",
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.secondary[200],
                borderRadius: "8px",
                visibility: "hidden",
                transition: "visibility 0.2s",
              },
              "&:hover::-webkit-scrollbar-thumb": {
                visibility: "visible",
              },
            }}
          >
            {renderNavItems(memoizedNavItems, active, onClickItem, theme)}
          </List>
          {/* Footer or version info */}
          <Box textAlign="center" py={0.5}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
              &copy; {new Date().getFullYear()} {config?.appName || ""}
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Sidebar;
